import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Quote, Star, Sparkles, Loader2 } from 'lucide-react';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.04] rounded-full blur-3xl" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
              <Sparkles size={14} className="mr-2" />What People Say
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Testimonials
            </h1>
            <p className="text-lg text-neutral-400 max-w-2xl mx-auto">What clients and colleagues say about working with me</p>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
              </div>
            ) : testimonials.length === 0 ? (
              <div className="text-center py-12">
                <Quote size={48} className="mx-auto text-neutral-500 mb-4" />
                <p className="text-neutral-400">No testimonials available yet.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {testimonials.map((testimonial, index) => (
                  <ScrollDepthReveal key={testimonial.testimonial_id} delay={Math.min(index * 0.08, 0.3)}>
                    <TiltCard maxTilt={5} hoverScale={1.012} testId={`testimonial-tilt-${index}`} className="group">
                      {/* Glow effect */}
                      <div className="absolute -inset-2 bg-gradient-to-r from-white/10 via-white/5 to-white/10 rounded-3xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Card */}
                      <div className={`relative bg-white/[0.04] backdrop-blur-xl rounded-3xl border border-white/10 group-hover:border-white/30 transition-all duration-500 shadow-xl shadow-black/20 group-hover:shadow-2xl overflow-hidden flex flex-col lg:flex-row ${index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`}>
                        
                        {/* Image Section - Large and Clear */}
                        <div className="lg:w-1/3 flex-shrink-0">
                          <div className="relative h-64 lg:h-full min-h-[280px] bg-white/5">
                            {testimonial.avatar ? (
                              <img
                                src={testimonial.avatar}
                                alt={testimonial.name}
                                className="w-full h-full object-cover object-top"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-white/10">
                                <span className="text-6xl font-bold text-white">
                                  {testimonial.name?.charAt(0) || '?'}
                                </span>
                              </div>
                            )}
                            {/* Gradient overlay */}
                            <div className={`absolute inset-0 bg-gradient-to-${index % 2 === 0 ? 'r' : 'l'} from-transparent via-transparent to-black/40 lg:block hidden`} />
                          </div>
                        </div>

                        {/* Content Section */}
                        <div className="lg:w-2/3 p-8 lg:p-10 flex flex-col justify-center">
                          {/* Quote Icon */}
                          <div className="mb-6">
                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg shadow-white/10 transform -rotate-6">
                              <Quote size={28} className="text-black" />
                            </div>
                          </div>

                          {/* Stars */}
                          <div className="flex items-center gap-1 mb-4">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>

                          {/* Testimonial Text */}
                          <blockquote className="text-neutral-300 text-lg leading-relaxed mb-6 italic">
                            "{testimonial.content}"
                          </blockquote>

                          {/* Author Info */}
                          <div className="flex items-center pt-4 border-t border-white/10">
                            <div>
                              <h4 className="font-bold text-xl text-white">{testimonial.name}</h4>
                              <p className="text-base bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent font-medium">
                                {testimonial.role}
                                {testimonial.company && (
                                  <span className="text-neutral-300">, {testimonial.company}</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TiltCard>
                  </ScrollDepthReveal>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-3xl p-10 lg:p-14 shadow-2xl shadow-white/10">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Let's Work Together
              </h2>
              <p className="text-neutral-700 text-lg mb-8 max-w-2xl mx-auto">
                Interested in collaborating? I'd love to hear from you and discuss how we can create something amazing together.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-4 bg-black text-white font-bold rounded-full hover:shadow-lg hover:bg-neutral-800 transition-all duration-300 transform hover:scale-105"
              >
                Get in Touch
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TestimonialsPage;
